import React from 'react';
import { UseControllerProps, useController, FieldValues } from 'react-hook-form';
import { Field, BaseFieldProps, WrappedFieldProps } from 'redux-form';

import dayjs from '@common/utils/dayjs';

import { DateInput, DateInputProps } from './date-input';

/**
 * By default the form validation mode is set to `mode: 'onSubmit'`.
 * This means the validation will be shown after submitting the form.
 * You probably want to set this option to `all` to have immediate feedback when a field does not pass the added rules.
 * useForm({ mode: 'all' });
 */
export function DateInputHookForm<T extends FieldValues>(
  props: Omit<DateInputProps, 'ref'> & UseControllerProps<T>
) {
  const { field, fieldState } = useController(props);

  const errorDisplay = props.error !== undefined && props.error !== null
    ? props.error
    : (fieldState.error?.message || fieldState.error?.message === '');

  return (
    <DateInput
      {...props}
      ref={field.ref}
      name={field.name}
      value={field.value}
      onBlur={() => field.onBlur()}
      onChange={(date) => field.onChange(date ? date.toISOString() : null)}
      error={errorDisplay}
    />
  );
}

const ReduxFormField = (props: WrappedFieldProps & DateInputProps) => {
  const { input, meta, ...rest } = props;

  return (
    <DateInput
      error={meta.touched && meta.error}
      warning={meta.touched && meta.warning}
      name={input.name}
      value={input.value}
      onBlur={() => input.onBlur(input.value)}
      onFocus={input.onFocus}
      {...rest}
      onChange={(date) => input.onChange(date ? dayjs(date) : null)}
    />
);
};

export const DatePickerInput = ({ name, ...props }: BaseFieldProps & DateInputProps) => (
  <Field
    {...props}
    name={name}
    component={ReduxFormField}
  />
);
